// ============================================================
// DEALIN MVP - Route Configuration
// ============================================================

import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation, ScrollRestoration } from 'react-router-dom';
import { Layout, LayoutNoFooter, LayoutMinimal } from '@/components/layout';
import { ProtectedRoute, PublicOnlyRoute } from './ProtectedRoute';

// Lazy load pages for better performance
import { lazy, Suspense, useEffect, useState } from 'react';
import { PageLoading } from '@/components/ui/Spinner';
import { capturePageView } from '@/lib/posthog';

const RootScrollWrapper = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  useEffect(() => {
    capturePageView(pathname + search);
  }, [pathname, search]);

  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
};

// Public pages
import HomeOrLanding from '@/pages/HomeOrLanding';
import NewLandingPage from '@/pages/NewLanding';
const HomePage = lazy(() => import('@/pages/Home'));
const VendorDetailPage = lazy(() => import('@/pages/VendorDetail'));
const ServiceDetailPage = lazy(() => import('@/pages/ServiceDetail'));
const TendersHomePage = lazy(() => import('@/pages/TendersHome'));
const TenderDetailPage = lazy(() => import('@/pages/TenderDetail'));
const EventsHomePage = lazy(() => import('@/pages/EventsHome'));
const EventDetailPage = lazy(() => import('@/pages/EventDetail'));

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/Login'));
const AuthFlowPage = lazy(() => import('@/pages/auth/AuthFlow'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPassword'));

// Protected pages
const ChatPage = lazy(() => import('@/pages/Chat'));
const ManagePage = lazy(() => import('@/pages/manage/ManagePanel'));
const ProfilePage = lazy(() => import('@/pages/Profile'));
const NotificationsPage = lazy(() => import('@/pages/Notifications'));

// Vendor-only pages (removed - now handled by ManagePanel tabs)

// Create pages
const CreateServicePage = lazy(() => import('@/pages/create/CreateService'));
const CreateBandoIntroPage = lazy(() => import('@/pages/create/CreateBandoIntro'));
const CreateBandoPage = lazy(() => import('@/pages/create/CreateBando'));
const CreateDealIntroPage = lazy(() => import('@/pages/create/CreateDealIntro'));
const CreateDealPage = lazy(() => import('@/pages/create/CreateDeal'));
const CreateEventPage = lazy(() => import('@/pages/create/CreateEvent'));
const AIBriefingIntroPage = lazy(() => import('@/pages/create/AIBriefingIntro'));
const BriefingWizardPage = lazy(() => import('@/pages/create/BriefingWizard'));
const DiagnosticPage = lazy(() => import('@/pages/diagnostic/Diagnostic'));
const BriefBuilderPage = lazy(() => import('@/pages/brief/BriefBuilder'));
const AIChatPage = lazy(() => import('@/pages/AIChat'));
const ConvertToDealPage = lazy(() => import('@/pages/brief/ConvertToDeal'));
const SharedAssessmentPage = lazy(() => import('@/pages/brief/SharedAssessment'));
const AssessmentsListPage = lazy(() => import('@/pages/brief/AssessmentsList'));

// Edit pages
const EditVendorPage = lazy(() => import('@/pages/vendor/EditVendor'));
const EditServicePage = lazy(() => import('@/pages/create/EditService'));
const EditTenderPage = lazy(() => import('@/pages/create/EditTender'));
const EditEventPage = lazy(() => import('@/pages/create/EditEvent'));
const AddReviewPage = lazy(() => import('@/pages/review/AddReview'));

// Tender pages
const TenderParticipatePage = lazy(() => import('@/pages/tender/TenderParticipate'));
const TenderApplicationsPage = lazy(() => import('@/pages/tender/TenderApplications'));
const TenderManageBandoPage = lazy(() => import('@/pages/tender/TenderManageBando'));
const TenderManageDealPage = lazy(() => import('@/pages/tender/TenderManageDeal'));

// Onboarding
const OnboardingPage = lazy(() => import('@/pages/onboarding/Onboarding'));

// Client profile
const ClientProfilePage = lazy(() => import('@/pages/client/ClientProfile'));

// Calendar
const CalendarPage = lazy(() => import('@/pages/Calendar'));

// SEO pages
const CustomSEOPage = lazy(() => import('@/pages/seo/CustomSEOPage'));
const SEOIndexPage = lazy(() => import('@/pages/SEOIndex'));

// Admin pages
const AdminAssessmentsPage = lazy(() => import('@/pages/admin/AdminAssessments'));
const AdminEmailPage = lazy(() => import('@/pages/admin/AdminEmail'));
const AdminArticlesPage = lazy(() => import('@/pages/admin/AdminArticles'));

// Articles (blog)
const ArticlesListPage = lazy(() => import('@/pages/ArticlesList'));
const ArticleDetailPage = lazy(() => import('@/pages/ArticleDetail'));

// Static pages
const FAQPage = lazy(() => import('@/pages/FAQ'));
const PrivacyPage = lazy(() => import('@/pages/Privacy'));
const TermsPage = lazy(() => import('@/pages/Terms'));
const ClaimProfilePage = lazy(() => import('@/pages/ClaimProfile'));
const ContactPage = lazy(() => import('@/pages/Contact'));

// Error page
const NotFoundPage = lazy(() => import('@/pages/NotFound'));

// Suspense wrapper
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoading />}>{children}</Suspense>
);

const L_COM_EXPERIMENT_STORAGE_KEY = 'ab:l-com:v1';

const LComExperimentPage = () => {
  const [variant] = useState<'lCom' | 'lComB'>(() => {
    if (typeof window === 'undefined') {
      return 'lCom';
    }

    const storedVariant = window.localStorage.getItem(L_COM_EXPERIMENT_STORAGE_KEY);
    if (storedVariant === 'lCom' || storedVariant === 'lComB') {
      return storedVariant;
    }

    const assignedVariant = Math.random() < 0.5 ? 'lCom' : 'lComB';
    window.localStorage.setItem(L_COM_EXPERIMENT_STORAGE_KEY, assignedVariant);
    return assignedVariant;
  });

  return <NewLandingPage variant={variant} canonicalOverride="https://www.deal-in.it/l-com" />;
};

// Router configuration — single root wrapper ensures scroll-to-top on every navigation
const router = createBrowserRouter([
  {
    element: <RootScrollWrapper />,
    children: [
  // Public routes with main layout
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <HomeOrLanding />,
      },
      {
        path: 'new-landing',
        element: <Navigate to="/" replace />,
      },
      {
        path: 'e2',
        element: (
          <SuspenseWrapper>
            <NewLandingPage variant="e2" />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'e-finanza-agevolata-2',
        element: (
          <SuspenseWrapper>
            <NewLandingPage variant="eFinanzaAgevolata2" />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'l-com',
        element: (
          <SuspenseWrapper>
            <LComExperimentPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'l-com-a',
        element: (
          <SuspenseWrapper>
            <NewLandingPage variant="lCom" />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'l-com-b',
        element: (
          <SuspenseWrapper>
            <NewLandingPage variant="lComB" />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'l-sec',
        element: (
          <SuspenseWrapper>
            <NewLandingPage variant="lSec" />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'home',
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <HomePage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'vendor/:vendorId',
        element: (
          <SuspenseWrapper>
            <VendorDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'vendor/:vendorId/service/:serviceId',
        element: (
          <SuspenseWrapper>
            <ServiceDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'client/:clientId',
        element: (
          <SuspenseWrapper>
            <ClientProfilePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'tenders',
        element: <Navigate to="/home" replace />,
      },
      {
        path: 'tenders/:tenderId',
        element: <Navigate to="/home" replace />,
      },
      {
        path: 'events',
        element: <Navigate to="/home" replace />,
      },
      {
        path: 'events/:eventId',
        element: <Navigate to="/home" replace />,
      },
      // Alias routes
      {
        path: 'aziende',
        element: <Navigate to="/home" replace />,
      },
      {
        path: 'vendors',
        element: <Navigate to="/home" replace />,
      },
      // Legacy routes - redirect to manage panel tabs
      {
        path: 'my-applications',
        element: (
          <ProtectedRoute>
            <Navigate to="/manage?page=dashboard" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'my-reviews',
        element: (
          <ProtectedRoute>
            <Navigate to="/manage?page=consulenze" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'manage/services',
        element: (
          <ProtectedRoute requireVendor>
            <Navigate to="/manage?page=servizi" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <ProfilePage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'notifications',
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <NotificationsPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'vendor-dashboard',
        element: (
          <ProtectedRoute requireVendor>
            <Navigate to="/manage?page=dashboard" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute requireVendor>
            <Navigate to="/manage?page=report" replace />
          </ProtectedRoute>
        ),
      },
      // SEO index page (hub for all SEO pages)
      {
        path: 'consulenza',
        element: (
          <SuspenseWrapper>
            <SEOIndexPage />
          </SuspenseWrapper>
        ),
      },
      // Custom SEO landing pages (static content)
      {
        path: 'consulenza/:slug',
        element: (
          <SuspenseWrapper>
            <CustomSEOPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settore/:slug',
        element: (
          <SuspenseWrapper>
            <CustomSEOPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'servizi-it/:slug',
        element: (
          <SuspenseWrapper>
            <CustomSEOPage />
          </SuspenseWrapper>
        ),
      },
      // Articles (blog)
      {
        path: 'articoli',
        element: (
          <SuspenseWrapper>
            <ArticlesListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'articoli/:slug',
        element: (
          <SuspenseWrapper>
            <ArticleDetailPage />
          </SuspenseWrapper>
        ),
      },
      // Static pages
      {
        path: 'faq',
        element: (
          <SuspenseWrapper>
            <FAQPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'privacy',
        element: (
          <SuspenseWrapper>
            <PrivacyPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'terms',
        element: (
          <SuspenseWrapper>
            <TermsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'richiesta-profilo',
        element: (
          <SuspenseWrapper>
            <ClaimProfilePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'contact',
        element: (
          <SuspenseWrapper>
            <ContactPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  // Manage Panel (header, no footer)
  {
    path: '/manage',
    element: <LayoutNoFooter />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <ManagePage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Chat with no-footer layout
  {
    path: '/chat',
    element: <LayoutNoFooter />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <ChatPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: ':threadId',
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <ChatPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Auth routes - full width (no layout wrapper)
  // Google Sign-In fallback route:
  // some mobile browsers/in-app webviews can land on /gsi/transform and get stuck.
  // We recover by sending users back to login with a retry hint.
  {
    path: '/gsi/*',
    element: <Navigate to="/login?google_flow=retry" replace />,
  },
  {
    path: '/login',
    element: (
      <PublicOnlyRoute>
        <SuspenseWrapper>
          <LoginPage />
        </SuspenseWrapper>
      </PublicOnlyRoute>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <PublicOnlyRoute>
        <SuspenseWrapper>
          <ForgotPasswordPage />
        </SuspenseWrapper>
      </PublicOnlyRoute>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <SuspenseWrapper>
        <ResetPasswordPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/assessment-ai',
    element: <Navigate to="/" replace />,
  },
  {
    path: '/ai-assessment',
    element: <Navigate to="/" replace />,
  },
  {
    path: '/register',
    element: (
      <PublicOnlyRoute>
        <SuspenseWrapper>
          <AuthFlowPage />
        </SuspenseWrapper>
      </PublicOnlyRoute>
    ),
  },
  // Dedicated URL to resume onboarding:
  // - if onboarding incomplete -> opens onboarding flow
  // - if onboarding complete -> redirects to /home
  {
    path: '/onboarding-start',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <AuthFlowPage onboardingOnly />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  // Onboarding
  {
    path: '/onboarding',
    element: <LayoutMinimal />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <OnboardingPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Calendar (disabled)
  {
    path: '/calendar',
    element: <Navigate to="/home" replace />,
  },
  // Create pages
  {
    path: '/vendor/:vendorId/services/add',
    element: (
      <ProtectedRoute requireVendor>
        <SuspenseWrapper>
          <CreateServicePage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/services/add',
    element: (
      <ProtectedRoute requireVendor>
        <SuspenseWrapper>
          <CreateServicePage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/service/create',
    element: (
      <ProtectedRoute requireVendor>
        <SuspenseWrapper>
          <CreateServicePage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  // Bando routes (disabled)
  {
    path: '/tenders/create-bando',
    element: <Navigate to="/home" replace />,
  },
  {
    path: '/tenders/create-bando/new',
    element: <Navigate to="/home" replace />,
  },
  // AI Briefing - Intro page
  {
    path: '/create/briefing',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <AIBriefingIntroPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  // AI Briefing - Wizard
  {
    path: '/create/briefing/wizard',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <BriefingWizardPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  // AI Diagnostic - Assessment guidato (legacy)
  {
    path: '/diagnostic',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <DiagnosticPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  // Brief Builder - Assessment strutturato
  {
    path: '/brief',
    element: (
      <SuspenseWrapper>
        <BriefBuilderPage />
      </SuspenseWrapper>
    ),
  },
  // AI Chat v2 - chat specialistica + shortlist RAG-only
  {
    path: '/aichat',
    element: (
      <SuspenseWrapper>
        <AIChatPage />
      </SuspenseWrapper>
    ),
  },
  // Brief to Bando/Deal conversion (disabled)
  {
    path: '/brief/:sessionId/convert',
    element: <Navigate to="/home" replace />,
  },
  // Assessment list
  {
    path: '/assessments',
    element: <LayoutNoFooter />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <AssessmentsListPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Shared Assessment (read-only view, with site header)
  {
    path: '/assessment/:sessionId',
    element: <LayoutNoFooter />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <SharedAssessmentPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Deal routes (disabled)
  {
    path: '/tenders/create-deal',
    element: <Navigate to="/home" replace />,
  },
  {
    path: '/tenders/create-deal/new',
    element: <Navigate to="/home" replace />,
  },
  // Events create (disabled)
  {
    path: '/events/create',
    element: <Navigate to="/home" replace />,
  },
  // Edit vendor profile (standalone route, also accessible via /manage?page=vetrina)
  {
    path: '/edit-vendor',
    element: (
      <ProtectedRoute requireVendor>
        <SuspenseWrapper>
          <EditVendorPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  // Edit service
  {
    path: '/vendor/:vendorId/services/:serviceId/edit',
    element: (
      <ProtectedRoute requireVendor>
        <SuspenseWrapper>
          <EditServicePage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  // Edit tender/deal (disabled)
  {
    path: '/tenders/:tenderId/edit',
    element: <Navigate to="/home" replace />,
  },
  // Edit event
  {
    path: '/events/:eventId/edit',
    element: <Navigate to="/home" replace />,
  },
  // Tender routes (disabled)
  {
    path: '/tenders/:tenderId/participate',
    element: <Navigate to="/home" replace />,
  },
  {
    path: '/tenders/:tenderId/applications',
    element: <Navigate to="/home" replace />,
  },
  {
    path: '/tenders/:tenderId/manage-bando',
    element: <Navigate to="/home" replace />,
  },
  {
    path: '/tenders/:tenderId/manage-deal',
    element: <Navigate to="/home" replace />,
  },
  // Admin pages
  {
    path: '/admin/assessments',
    element: <LayoutNoFooter />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <AdminAssessmentsPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '/admin/email',
    element: <LayoutNoFooter />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <AdminEmailPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '/gestione/articoli',
    element: <LayoutNoFooter />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <AdminArticlesPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // 404
  {
    path: '*',
    element: <Layout />,
    children: [
      {
        path: '*',
        element: (
          <SuspenseWrapper>
            <NotFoundPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  // Review pages
  {
    path: '/vendor/:vendorId/reviews/add',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <AddReviewPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
    ], // end RootScrollWrapper children
  },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
